import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import PropTypes from 'prop-types';
import event from '../../../utils/analytics/event';
import { useSubscriptionActions } from '../../../store/subscription/hooks';
import { SubscribeContainer, InputContainer, StatusText, SubscribeInput, SubscribeButton } from './SubscribeForm.styles';
/**
 * A component that completely handles creating a subscriber. In order for this component to work correctly, you must first integrate the Pantheon `subscription` model into your app.
 *
 * This is NOT themable, but uses the `Button`, `BodyText` and `Input` components, which can be themed.
 *
 * ## Piwik Event tracking
 *
 * By default, it logs 3 possible piwik events:
 *
 * - `event(['click', 'subscribe', <page being shared>]);`
 * - `event(['auth', 'subscriber-create', <lucina id>]);`
 * - `event(['auth', 'subscriber-failure', <error>]);`
 *
 * If using the default piwik events, then a value for the `page` prop must be passed in.
 *
 * If an object is passed for the piwikEvents param, then 3 custom piwik events will be logged instead of the default options.
 *
 * `piwikEvents` must be passed in as such: `{start: [], success:[], failure:[]}`
 *
 * ## Customization
 *
 * Use the `font` prop to customize the font-family for this form. Additionally, you can also change the button text (using the `buttonText` prop) or input placeholder (using the `inputPlaceholder` prop) if you desire to do so.
 *
 * The success and failure status messages can also be customized using `successText` and `failureText` props respectively.
 *
 * By default, the success text is `Thanks, please go confirm your email to complete your subscription!` and the failure text is `Something went wrong. Please try to enter a different email or try again later` or the specific error message coming from Lucina.
 *
 * You can also customize the success and failure text colors using the `successColor` and `failureColor` props.
 *
 * ## Example Usage
 *
 * ```js
 * import { SubscribeForm } from '@minerva-analytics/pantheon';
 *
 * const requestConfig = {
 *   url: `http://localhost:8080/subscribers/`,
 *   headers: { 'X-Lucina-Domain': 'examplesite.com' },
 * };
 *
 * <SubscribeForm requestConfig={requestConfig} page="homepage"/>
 * ```
 */

var SubscribeForm = function SubscribeForm(props) {
  var _useSubscriptionActio = useSubscriptionActions(),
      createSubscriber = _useSubscriptionActio.createSubscriber,
      updateEmail = _useSubscriptionActio.updateEmail;

  var initialMessage = props.initialStatusMessage || '';

  var _useState = useState(initialMessage),
      errorMessage = _useState[0],
      setErrorMessage = _useState[1];

  var successMessage = props.successText || 'Thanks, please go confirm your email to complete your subscription!';
  var status = useSelector(function (state) {
    return state.subscription.createSubscriberStatus;
  });
  var email = useSelector(function (state) {
    return state.subscription.email;
  });

  var subscribe = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var response;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              event(props.piwikEvents && props.piwikEvents.start ? props.piwikEvents.start : ['click', 'subscribe', props.page]);
              _context.prev = 1;
              _context.next = 4;
              return createSubscriber(email, props.requestConfig);

            case 4:
              response = _context.sent;
              event(props.piwikEvents && props.piwikEvents.success ? props.piwikEvents.success : ['auth', 'subscriber-create', "" + response.data.id]);
              props.successHandler();
              _context.next = 14;
              break;

            case 9:
              _context.prev = 9;
              _context.t0 = _context["catch"](1);
              setErrorMessage(props.failureText || 'Something went wrong. Please try to enter a different email or try again later');
              event(props.piwikEvents && props.piwikEvents.failure ? props.piwikEvents.failure : ["auth", "subscriber-failure", _context.t0.response ? JSON.stringify(_context.t0.response.data) : _context.t0.message]);

              if (_context.t0.response) {
                if (_context.t0.response.data.non_field_errors && _context.t0.response.data.non_field_errors.length >= 1) {
                  setErrorMessage(props.failureText || _context.t0.response.data.non_field_errors[0]);
                } else if (_context.t0.response.data.email && _context.t0.response.data.email.length >= 1) {
                  setErrorMessage(props.failureText || _context.t0.response.data.email[0]);
                }
              }

            case 14:
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });

            case 15:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[1, 9]]);
    }));

    return function subscribe() {
      return _ref.apply(this, arguments);
    };
  }();

  var statusMessage = status.loaded && !status.failed ? successMessage : errorMessage;

  var submitOnEnter = function submitOnEnter(e) {
    if (e.key === 'Enter') {
      subscribe();
    }
  };

  return /*#__PURE__*/React.createElement(SubscribeContainer, {
    className: props.className || '',
    font: props.font,
    successHandler: props.successHandler,
    stackedLayout: props.stackedLayout
  }, /*#__PURE__*/React.createElement(InputContainer, {
    stackedLayout: props.stackedLayout
  }, /*#__PURE__*/React.createElement(SubscribeInput, {
    type: "email",
    name: "email",
    onChange: function onChange(e) {
      return updateEmail(e.target.value);
    },
    value: email,
    placeholder: props.inputPlaceholder || 'Enter your email...',
    onKeyDown: submitOnEnter,
    stackedLayout: props.stackedLayout
  }), /*#__PURE__*/React.createElement(SubscribeButton, {
    onClick: function onClick() {
      return subscribe();
    },
    disabled: status.loading
  }, /*#__PURE__*/React.createElement("div", null, status.loading ? 'Subscribing...' : props.buttonText || 'Subscribe'), status.loading && /*#__PURE__*/React.createElement(CircularProgress, {
    size: 20,
    style: {
      marginLeft: '10px',
      color: '#fff'
    }
  }))), /*#__PURE__*/React.createElement(StatusText, {
    color: status.failed ? props.failureColor : props.successColor
  }, status.loading ? "" : statusMessage));
};

SubscribeForm.propTypes = {
  /** The page that this component is being used on. Not used if custom Piwik events are used. */
  page: PropTypes.string,

  /** Optional custom text for when subscriber creation is successful */
  successText: PropTypes.string,

  /** Optional custom text for when subscriber creation is unsuccessful */
  failureText: PropTypes.string,

  /** Optional initial custom status text */
  initialStatusMessage: PropTypes.string,

  /** Optional custom color for success status text */
  successColor: PropTypes.string,

  /** Optional custom color for failure status text */
  failureColor: PropTypes.string,

  /** Optional custom font for any text within the SubscribeForm */
  font: PropTypes.string,

  /** A custom request config to pass to `createSubscriber` */
  requestConfig: PropTypes.object.isRequired,

  /**
   * A custom classname to apply to the container. Usually provided automatically
   * by styled-components by doing `styled(SubscribeForm)`
   */
  className: PropTypes.string,

  /** An optional object consisting of 3 custom events: start, success & failure. Object keys must be `start`, `success` and `failure` with values as arrays in the form of [category, action, name, value], though name and value are optional. */
  piwikEvents: PropTypes.object,

  /** Optional custom text for the submit button */
  buttonText: PropTypes.string,

  /** Optional custom placeholder text for the input */
  inputPlaceholder: PropTypes.string,

  /** Optional custom function for handling success functionality, called after createSubscriber finishes successfully */
  successHandler: PropTypes.func,

  /** Optional prop passed in if a stacked layout is desired */
  stackedLayout: PropTypes.bool
};
SubscribeForm.defaultProps = {
  buttonText: 'Subscribe',
  inputPlaceholder: 'Enter your email...'
};
export default SubscribeForm;